import { IVec3 } from "@base/math/Vec3";
import { PartialRecord, TupleToUnion } from "@base/util/TypeUtil";
import { ModelTemplateId } from "@biz/definitions/ModelDefinitions";
import { IProductData } from "@biz/definitions/ProductDefinitions";
import { IClothItemData } from "@biz/logic/ClothData";

export const sojoModes = ["mid", "top", "bottom", "butterfly"] as const;
export type SojoMode = TupleToUnion<typeof sojoModes>
export type SojoWoodType = "FA001" | "FA002" | "FA003"

export const sojoTemplateIds = ["sojo_sunbed", "sojo_sunbed_small", "sojo_sunseat"] satisfies ModelTemplateId[];
export type SojoTemplateId = TupleToUnion<typeof sojoTemplateIds>;

export const sojoInfos: Record<SojoTemplateId, {
  hw: string;
  cloth: string;
  rainSleeve: string;
}> = {
  sojo_sunbed: { hw: "6M07790", cloth: "6B0779", rainSleeve: "6B10790" },
  sojo_sunseat: { hw: "6M08790", cloth: "6B0879", rainSleeve: "6B10890" },
  sojo_sunbed_small: { hw: "6M18790", cloth: "6B1879", rainSleeve: "6B11890" },
}


export interface ILoungeItemTypeInfo {
  loungeType: LoungeType;
  width: number;
  depth: number;
  isCorner?: boolean;
  standalone?: boolean;
  armRestPossible?: boolean;
  pillowPossible?: boolean;
  articlePriceListId?: string;
}

export const LoungeItemTypeInfos = {
  "75": { loungeType: "Reload", width: 0.75, depth: 0.75, pillowPossible: true, armRestPossible: true, articlePriceListId: "SS" },
  "100": { loungeType: "Reload", width: 1.0, depth: 0.75, pillowPossible: true, armRestPossible: true, articlePriceListId: "DS" },
  "75_Eck": { loungeType: "Reload", width: 0.9, depth: 0.9, pillowPossible: true, isCorner: true, articlePriceListId: "CSS" },
  "Table_300": { loungeType: "Reload", width: 0.335, depth: 0.75, articlePriceListId: "ST300" },
  "Table_600": { loungeType: "Reload", width: 0.635, depth: 0.75, articlePriceListId: "ST600" },
  "Table_Lounge": { loungeType: "Reload", standalone: true, width: 0.635, depth: 0.90, articlePriceListId: "STLT" },

  "SS": { loungeType: "Unplugged", width: 0.9, depth: 0.9 },
  "CS": { loungeType: "Unplugged", width: 0.9, depth: 0.9, isCorner: true },
  "LO": { loungeType: "Unplugged", width: 0.9, depth: 1.8 },
  "FS": { loungeType: "Unplugged", width: 0.9, depth: 0.9 },
} as const;

type filterTypeInfos<T extends Record<string, ILoungeItemTypeInfo>, LT extends LoungeType> = {
  [P in keyof T as T[P] extends { loungeType: LT } ? P : never]: T[P]
};

export type LoungeItemType = keyof typeof LoungeItemTypeInfos;
export type ReloadType = keyof filterTypeInfos<typeof LoungeItemTypeInfos, "Reload">;
export type UnpluggedType = keyof filterTypeInfos<typeof LoungeItemTypeInfos, "Unplugged">;

export type ReloadMaterial = "steel" | "zinc";
export interface IReloadGlobalData extends IClothItemData {
  material: ReloadMaterial;
}

export type ReloadArmRestType = "bar";

export type IReloadChairSide = "left" | "right";

export interface IReloadChairArmRest {
  armRest?: PartialRecord<IReloadChairSide, ReloadArmRestType>;
  sidePillow?: PartialRecord<IReloadChairSide, boolean>;
}

interface ILoungeDataBase extends IProductData {
  itemType: "lounge";
  position: IVec3;
  rotationY: number;
}

export interface ILoungeReloadItem extends Partial<IReloadGlobalData>, IReloadChairArmRest {
  itemType: ReloadType;
  pillow?: boolean;
}
export interface ILoungeReloadData extends ILoungeDataBase, IReloadChairArmRest {
  loungeType: "Reload";
  global: IReloadGlobalData;
  chains: { items: ILoungeReloadItem[] }[];
}

export type IUnpluggedGlobalData = IClothItemData

export interface ILoungeUnpluggedItem extends Partial<IUnpluggedGlobalData> {
  itemType: UnpluggedType;
}

export interface ILoungeUnpluggedData extends ILoungeDataBase {
  loungeType: "Unplugged";
  global: IUnpluggedGlobalData;
  chains: { items: ILoungeUnpluggedItem[] }[];
}

export type ILoungeData = ILoungeReloadData | ILoungeUnpluggedData;
export type LoungeType = ILoungeData["loungeType"];
export type ILoungeItem = ILoungeReloadItem | ILoungeUnpluggedItem;

